import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import "./rules.css";
import { setBackButtonUrl } from "../uiSlice";
import { LOGIN_BACK_PATH } from "../gameSetupStorage";
import {
  getLandingPageTitleColor,
  getThemeButtonGradient,
  getThemeButtonTextColor,
} from "../../functions/themeAssets";

/* Bin Toss icon + badge color per rule. Admins write their own rules, so the icon is
   picked from what the rule talks about (first match wins); anything else cycles
   through RULE_FALLBACK_ICONS. */
const RULE_ICON_MATCHERS = [
  { test: /\bwrong|deduct|minus|penalt|\blos[et]\b/i, icon: "fa-circle-minus", color: "#ef3b5d" },
  { test: /\bright\b|\bcorrect|\bscore|\bpoints?\b|\bearn/i, icon: "fa-star", color: "#f5a524" },
  { test: /\bdrag|\bdrop|\bsort|\btoss|\bthrow/i, icon: "fa-hand-pointer", color: "#2f6fed" },
  { test: /\bbins?\b|dustbin|\bbaskets?\b/i, icon: "fa-trash-can", color: "#16a34a" },
  { test: /\bwaste|\bmaterials?\b|\bitems?\b|garbage|\btrash|recycl|rubbish/i, icon: "fa-recycle", color: "#14b8a6" },
  { test: /\btimer?\b|\bfast|\bquick|\bseconds?\b|\bminutes?\b/i, icon: "fa-stopwatch", color: "#9333ea" },
];

const RULE_FALLBACK_ICONS = [
  { icon: "fa-recycle", color: "#14b8a6" },
  { icon: "fa-leaf", color: "#16a34a" },
  { icon: "fa-trash-can", color: "#2f6fed" },
  { icon: "fa-hand-pointer", color: "#f5a524" },
  { icon: "fa-star", color: "#9333ea" },
];

function ruleIcon(rule, index) {
  return (
    RULE_ICON_MATCHERS.find((m) => m.test.test(rule)) ||
    RULE_FALLBACK_ICONS[index % RULE_FALLBACK_ICONS.length]
  );
}

export default function UserRules() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { status, user } = useSelector((s) => s.auth);
  const { status: themeStatus, data: themeData } = useSelector((s) => s.theme);

  useEffect(() => {
    dispatch(setBackButtonUrl(LOGIN_BACK_PATH));
  }, [dispatch, status, user]);

  /* Unlock document scroll so logo + Back scroll away with the rules list.
   * Login CSS otherwise keeps body.common-bg overflow:hidden on mobile portrait. */
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById("root");
    const snap = [];
    const setImp = (el, prop, value) => {
      if (!el) return;
      snap.push([
        el,
        prop,
        el.style.getPropertyValue(prop),
        el.style.getPropertyPriority(prop),
      ]);
      el.style.setProperty(prop, value, "important");
    };

    html.classList.add("sl-rules-open");
    body.classList.add("sl-rules-open");

    setImp(html, "height", "auto");
    setImp(html, "max-height", "none");
    setImp(html, "overflow-x", "hidden");
    setImp(html, "overflow-y", "auto");
    setImp(html, "overflow", "auto");

    setImp(body, "height", "auto");
    setImp(body, "min-height", "100%");
    setImp(body, "max-height", "none");
    setImp(body, "overflow", "visible");
    setImp(body, "overflow-x", "hidden");
    setImp(body, "overflow-y", "visible");
    setImp(body, "position", "static");
    setImp(body, "background-attachment", "scroll");
    setImp(body, "touch-action", "pan-y");

    setImp(root, "height", "auto");
    setImp(root, "min-height", "100%");
    setImp(root, "max-height", "none");
    setImp(root, "overflow", "visible");

    return () => {
      html.classList.remove("sl-rules-open");
      body.classList.remove("sl-rules-open");
      snap.forEach(([el, prop, value, priority]) => {
        if (value) el.style.setProperty(prop, value, priority || undefined);
        else el.style.removeProperty(prop);
      });
    };
  }, []);

  // Admin theme button colors feed the title ribbon and Next button (see rules.css).
  const themeStyle = useMemo(() => {
    if (!themeData) return undefined;
    const { start, end } = getThemeButtonGradient(themeData);
    const text = getThemeButtonTextColor(themeData);
    const style = {};
    if (start) {
      style["--rl-start"] = start;
      style["--rl-end"] = end || start;
    }
    if (text) style["--rl-text"] = text;
    return style;
  }, [themeData]);

  const rules = Array.isArray(themeData?.rules)
    ? themeData.rules.map((r) => String(r || "").trim()).filter(Boolean)
    : [];

  // Admin "Text Color" (Rules & Game Settings) is saved as landing_page_title_color.
  const titleColor = getLandingPageTitleColor(themeData) || "#3b2a1a";

  const ruleItems = rules.length ? rules : ["No rules defined"];

  if (themeStatus === "loading" && !themeData) {
    return (
      <div className="quiz-loader-container">
        <div className="quiz-loader" />
      </div>
    );
  }

  return (
    <div className="sl-rules-page" style={themeStyle}>
      <div className="sl-rules-card">
        <div className="sl-rules-ribbon">
          <span className="sl-rules-spark sl-rules-spark--left" aria-hidden="true" />
          <h1 className="sl-rules-ribbon-text">Rules</h1>
          <span className="sl-rules-spark sl-rules-spark--right" aria-hidden="true" />
        </div>

        <ul className="sl-rules-list">
          {ruleItems.map((rule, i) => {
            const { icon, color } = ruleIcon(rule, i);
            return (
              <li key={`${i}-${rule.slice(0, 12)}`} className="sl-rules-item">
                <span className="sl-rules-icon" style={{ "--icon-color": color }}>
                  <i className={`fa-solid ${icon}`} aria-hidden="true" />
                </span>
                <span className="sl-rules-text" style={{ color: titleColor }}>
                  {rule}
                </span>
              </li>
            );
          })}
        </ul>

        <button
          type="button"
          className="sl-rules-next"
          onClick={() => navigate("/game")}
        >
          <span>Next</span>
          <i className="fa-solid fa-arrow-right" aria-hidden="true" />
        </button>
      </div>

      <div className="sl-rotate-overlay" aria-hidden>
        Please rotate your device for the best experience
      </div>
    </div>
  );
}
